/**
 * Settings Service
 * Admin-tunable runtime settings (BMF-117) — the wait before an unpaid online
 * order is auto-cancelled, and the static UPI QR delivery agents show at the door.
 *
 * The delivery-fee GST helpers were removed by BMF-155 along with the screen
 * that used them: the fee now inherits each line's product rate, so there is no
 * single rate to pick. The backend endpoint survives for compatibility.
 */
import api from './api';

/** Kept in step with the backend's PAYMENT_QR_* constants. */
export const PAYMENT_QR_ACCEPT = 'image/jpeg,image/png';
export const PAYMENT_QR_MAX_BYTES = 5 * 1024 * 1024;
export const PAYMENT_QR_ALLOWED_LABEL = 'JPG or PNG';

const settingsService = {
  /**
   * Current General Settings values plus the bounds the server will accept.
   * The fallback data mirrors the backend defaults so the form still renders
   * something sane if the request fails.
   */
  async getGeneralSettings() {
    try {
      const response = await api.get('/settings/admin/general');
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch settings',
        data: {
          autoCancelMinutes: 10,
          autoCancelMinutesMin: 2,
          autoCancelMinutesMax: 120,
          paymentQrImageUrl: null,
        },
      };
    }
  },

  /**
   * Replace the UPI QR image delivery agents show when collecting a COD order.
   * The server re-checks type and size, so the caller's pre-check is only there
   * to fail fast without a wasted upload.
   */
  async uploadPaymentQr(file) {
    try {
      const form = new FormData();
      form.append('image', file);
      const response = await api.post('/settings/admin/payment-qr', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.message;
      return {
        success: false,
        error: Array.isArray(message) ? message[0] : message || 'Failed to upload the payment QR',
      };
    }
  },

  async updateAutoCancelMinutes(minutes) {
    try {
      const response = await api.patch('/settings/admin/order-auto-cancel-minutes', { minutes });
      return { success: true, data: response.data };
    } catch (error) {
      // The backend sends class-validator's array of messages on a 400; take the
      // first so the range error reaches the admin verbatim.
      const message = error.response?.data?.message;
      return {
        success: false,
        error: Array.isArray(message) ? message[0] : message || 'Failed to update setting',
      };
    }
  },
};

export default settingsService;
